/**
 * @frozen CP-3 v1.6 (frozen-contract rule: scripts/guards.mjs)
 * v1.6 (ADR-0066, F16.0c): additive: Guard `humanTeamsHaveStrategy` (listed on OPEN_ROUND), GuardContext.humanTeamsMissingStrategy (optional; absent passes).
 * CP-3 — Round state machine (facilitated) + solo variant + the staged finale, as typed transition tables.
 *
 * Forecast (OD5-1, "call the year"): the round's one twin forecast is a participant write in `open` (solo: `deciding`); there
 * is no re-lock, so no decision edit touches forecast state. Submit (team, solo) needs it; close defaults and rescues do not.
 *
 * Pure module: no I/O. The API's RoundService loads a GuardContext inside the same
 * transaction that row-locks the round (SELECT … FOR UPDATE), calls `transition()`,
 * then executes the returned effects in order (DB effects in-tx; jobs/SSE via outbox after commit).
 *
 * Timing (OD4-5, ADR-0015): the FACILITATOR opens and closes every round by hand. A round may carry an optional
 * display countdown (round.countdown_ends_at) shown to teams with a 5-minute warning, computed client-side.
 * The countdown is NEVER enforced: reaching zero changes nothing. There are no deadline jobs. Default durations
 * are editable program settings (cohort.config.runOfShow), not rules.
 *
 * Close (OD4-7, ADR-0019 §3; facilitated rounds only). There is no sign-off at close. LOCK
 * first applies card defaults to unanswered planned events (planned events are due at close), then:
 *  1. RESCUE: for each team the facilitator ticked in the close dialog (LOCK.rescueTeamIds), that team's CURRENT
 *     draft goes in as UNATTESTED (source 'facilitator_rescue'; flagged; governance evidence `unattestedSubmission`);
 *  2. DEFAULT for every other unsigned human team: its last CRO-attested version (source 'deadline_attested'); if it
 *     never attested one this round, last year's plan (source 'default_carry_forward'; R1 = STANDING_POLICY).
 * Only unsigned teams whose current draft is NOT the attested version can be rescued (GuardContext.rescuableTeamIds).
 * The 5-minute warning banner (teams with no attested version) is client-side, off RoundSummary.countdownEndsAt
 * and DraftView.closeFallback. Solo has no close-by-deadline, so none of this applies there.
 *
 * Rollback: there is no "rolledBack" state. ROLLBACK moves resolved|released back to locked|open and
 * SUPERSEDES (round_result.superseded_at, ai_generation.review_state='superseded'); nothing is deleted.
 * Only the last resolved or released round can be rolled back (later rounds must be draft), and never once the
 * cohort is completed (final round released). Re-resolution creates round_result generation n+1.
 *
 * Live surprise (ADR-0015): the facilitator may hold back ONE event card per round (HOLD_SURPRISE, before RESOLVE):
 *   locked → resolving (phase 1: engine halts at the surprise month; round_result.status = halted)
 *          → awaitingSurprise (RELEASE_SURPRISE delivers the card; affected teams respond)
 *          → resolvingPhase2 (CONTINUE_SURPRISE by the facilitator: defaults for non-responders, full replay)
 *          → resolved.
 * PAUSE works in awaitingSurprise too; pausing freezes the round countdown and the surprise countdown.
 *
 * Stuck resolve: the worker MUST fire RESOLVE_FAILED when pg-boss gives up (onFail). As a last resort the
 * facilitator may ABORT_RESOLVE a result older than GuardContext.resolveStaleAfterMs (3 × p95, config).
 */

export const ROUND_STATES = ['draft', 'open', 'locked', 'resolving', 'awaitingSurprise', 'resolvingPhase2', 'resolved', 'released', 'paused'] as const;
export type RoundState = (typeof ROUND_STATES)[number];
/** States a round can be paused from (and resumed back to). */
export const PAUSABLE_STATES = ['open', 'locked', 'awaitingSurprise'] as const satisfies readonly RoundState[];
export type PausableState = (typeof PAUSABLE_STATES)[number];

/** Minutes of a display countdown; null = no countdown. Range is validated by the route schema (1–240). */
type Countdown = number | null;

export type RoundEvent =
  | { type: 'OPEN'; countdownMinutes: Countdown }
  | { type: 'EXTEND'; addMinutes: number }                        // start or extend the display countdown
  | { type: 'LOCK'; force: boolean; rescueTeamIds: readonly string[] } // OD4-7 rescue: submit these teams' current drafts as unattested
  | { type: 'REOPEN'; countdownMinutes: Countdown }
  | { type: 'PAUSE' }
  | { type: 'RESUME' }
  | { type: 'RESOLVE' }
  | { type: 'RESOLVE_SUCCEEDED'; resultId: string }
  | { type: 'RESOLVE_FAILED'; resultId: string; message: string }
  | { type: 'ABORT_RESOLVE'; reason: string }                     // facilitator escape for a stuck worker
  | { type: 'HOLD_SURPRISE'; previewId: string }                  // one card per round, held back from OPEN delivery
  | { type: 'CANCEL_SURPRISE' }
  | { type: 'SURPRISE_HALTED'; resultId: string; month: number }  // worker: phase 1 stopped at the surprise month
  | { type: 'RELEASE_SURPRISE'; countdownMinutes: Countdown }     // deliver the card (+ optional display countdown)
  | { type: 'CONTINUE_SURPRISE'; force: boolean }                 // facilitator only (OD4-5): force = continue with defaults
  | { type: 'RELEASE'; waiveNarrativeWait: boolean }
  | { type: 'ROLLBACK'; target: 'locked' | 'open'; reason: string; countdownMinutes?: Countdown };
export type RoundEventType = RoundEvent['type'];

/** One actor vocabulary for round_transition.actor_kind AND audit_log.actor_kind. */
export const ACTOR_KINDS = ['facilitator', 'player', 'worker', 'system'] as const;
export type Actor = (typeof ACTOR_KINDS)[number];

/** Facts the service gathers (in-tx) before evaluating guards. */
export interface GuardContext {
  readonly now: Date;
  readonly cohortStatus: 'setup' | 'active' | 'completed' | 'archived';
  readonly roundNo: number;
  readonly roundCount: number;
  readonly previousRoundState: RoundState | null; // null for round 1
  readonly otherRoundInFlight: boolean;           // another round in open|locked|resolving|awaitingSurprise|resolvingPhase2|resolved|paused
  readonly humanTeamsTotal: number;
  readonly humanTeamsSubmitted: number;
  readonly humanTeamsMissingStrategy?: number;    // ADR-0066: human teams with no strategy written; absent = not gathered (the guard passes)
  readonly humanTeamsMissingCeoOrCro: number;     // human teams without a seated CEO or CRO label
  readonly rescuableTeamIds: readonly string[];   // OD4-7: unsigned human teams whose current draft is not the CRO-attested version
  readonly surpriseStatus: 'none' | 'held' | 'delivered';   // this round's live surprise (event_injection.mode = 'surprise')
  readonly surprisePendingResponses: number;      // affected teams that have not yet answered the delivered surprise
  readonly currentResultId: string | null;
  readonly currentResultStatus: 'running' | 'halted' | 'succeeded' | 'failed' | null;
  readonly currentResultStartedAt: Date | null;
  readonly resolveStaleAfterMs: number;           // 3 × resolve p95 (config; ADR-0022)
  readonly resultInvariantsPassed: boolean | null;
  readonly narrativesGenerating: number;          // narrative jobs still running for the current result
  readonly laterRoundsAllDraft: boolean;
  readonly pausedFrom: PausableState | null;
}

export type Guard =
  | 'cohortActive' | 'previousRoundReleased' | 'noOtherRoundInFlight' | 'humanTeamsHaveCeoAndCro' | 'humanTeamsHaveStrategy'
  | 'addMinutesPositive' | 'surpriseDeliveredIfAwaiting' | 'allHumanTeamsSubmittedOrForced' | 'rescueTeamsRescuable'
  | 'noResultRunning' | 'resultIsCurrentGeneration' | 'invariantsPassed' | 'resultStale'
  | 'narrativesGeneratedOrWaived' | 'laterRoundsDraft' | 'reasonProvided' | 'cohortNotCompleted' | 'hasPausedFrom'
  | 'noSurpriseHeld' | 'surpriseNotDelivered' | 'surpriseDelivered' | 'surpriseAnsweredOrForced';

type GuardFn = (ctx: GuardContext, e: RoundEvent, from: RoundState) => boolean;
export const GUARDS: { readonly [G in Guard]: GuardFn } = {
  cohortActive: (c) => c.cohortStatus === 'active',
  previousRoundReleased: (c) => c.previousRoundState === null || c.previousRoundState === 'released',
  noOtherRoundInFlight: (c) => !c.otherRoundInFlight,
  humanTeamsHaveCeoAndCro: (c) => c.humanTeamsMissingCeoOrCro === 0,
  humanTeamsHaveStrategy: (c) => (c.humanTeamsMissingStrategy ?? 0) === 0,
  addMinutesPositive: (_c, e) => e.type === 'EXTEND' && e.addMinutes > 0,
  surpriseDeliveredIfAwaiting: (c, _e, from) => from !== 'awaitingSurprise' || c.surpriseStatus === 'delivered',
  allHumanTeamsSubmittedOrForced: (c, e) => (e.type === 'LOCK' && e.force) || c.humanTeamsSubmitted >= c.humanTeamsTotal,
  rescueTeamsRescuable: (c, e) => e.type !== 'LOCK' || e.rescueTeamIds.every((id) => c.rescuableTeamIds.includes(id)),
  noResultRunning: (c) => c.currentResultStatus !== 'running',
  resultIsCurrentGeneration: (c, e) => (e.type === 'RESOLVE_SUCCEEDED' || e.type === 'RESOLVE_FAILED' || e.type === 'SURPRISE_HALTED') && e.resultId === c.currentResultId,
  invariantsPassed: (c) => c.resultInvariantsPassed === true,
  resultStale: (c) => c.currentResultStartedAt !== null && c.now.getTime() - c.currentResultStartedAt.getTime() > c.resolveStaleAfterMs,
  narrativesGeneratedOrWaived: (c, e) => (e.type === 'RELEASE' && e.waiveNarrativeWait) || c.narrativesGenerating === 0,
  laterRoundsDraft: (c) => c.laterRoundsAllDraft,
  reasonProvided: (_c, e) => (e.type === 'ROLLBACK' || e.type === 'ABORT_RESOLVE') && e.reason.trim().length >= 10,
  cohortNotCompleted: (c) => c.cohortStatus !== 'completed',
  hasPausedFrom: (c) => c.pausedFrom !== null,
  noSurpriseHeld: (c) => c.surpriseStatus === 'none',
  surpriseNotDelivered: (c) => c.surpriseStatus === 'held',
  surpriseDelivered: (c) => c.surpriseStatus === 'delivered',
  surpriseAnsweredOrForced: (c, e) => (e.type === 'CONTINUE_SURPRISE' && e.force) || c.surprisePendingResponses === 0,
};

/**
 * Side-effects, executed in listed order. `tx:` = inside the state-change transaction;
 * `job:` = pg-boss send (in-tx via pg-boss `db` adapter so it commits atomically);
 * `sse:` = outbox row (in-tx), fanned out after commit (EFFECT_TO_SSE in cp3_contracts maps them to SSE types).
 */
export type RoundEffect =
  | 'tx:setCountdown' | 'tx:clearCountdown'   // round.countdown_ends_at (display only; null = none)
  | 'tx:freezeCountdowns' | 'tx:restoreCountdowns' // PAUSE/RESUME: round countdown AND delivered-surprise countdowns shift by the pause
  | 'tx:deliverRoundStartEvents'           // scheduled + conditional cards → event_injection rows (mode=planned; a held surprise is skipped)
  | 'tx:holdSurprise' | 'tx:cancelSurprise' // event_injection rows mode=surprise, status held|cancelled; round.surprise_group_id
  | 'tx:markResultHalted'                  // round_result.status=halted, checkpoint_month; resolved_facts written onto the held rows
  | 'tx:deliverSurpriseEvent'              // held → delivered (+ optional response countdown)
  | 'tx:supersedeHaltedResult'             // phase-1 generation superseded (reason 'surprise_phase2'); never deleted
  | 'tx:applyDefaultEventChoices'          // unanswered injections → event_response(is_default)
  | 'tx:submitRescuedDraftsUnattested'    // OD4-7 rescue: each LOCK.rescueTeamIds team → its CURRENT draft as submission
                                           // (source=facilitator_rescue, flagged, unattested, rescued_by) + evidence unattestedSubmission
  | 'tx:submitCloseDefaults'               // OD4-7 default: every other unsigned human team → its last CRO-attested version
                                           // (source=deadline_attested) or, if none, last year's plan (default_carry_forward; R1 = autopilot)
  | 'tx:generatePersonaSubmissions'        // AI seats: persona + Expander → submission(source=persona)
  | 'tx:freezeInputsAndHash'               // new round_result(generation+1, running, inputs_hash, manifest)
  | 'tx:markResultSucceeded' | 'tx:markResultFailed'
  | 'tx:markResultAborted'                 // ABORT_RESOLVE: status failed, error 'aborted_by_facilitator'; late worker events are rejected
  | 'tx:publishMarket'                     // released_at; results + public market data (PublishedMarket peers) readable;
                                           // narratives auto-publish per narrativeDisposition (CP-1); held ones wait for approval.
                                           // Final round: participant views withhold the final rank until finale stage 'rank' (OD5-5)
  | 'tx:completeCohortIfFinal'             // final round: cohort.status = completed, finale_stage = notStarted (post-check released separately, OD4-5)
  | 'tx:supersedeResult'                   // round_result.superseded_at + ai_generation superseded + evidence superseded
  | 'job:resolveRound'                     // singletonKey = `resolve:${resultId}`; onFail → RESOLVE_FAILED
  | 'job:generateNarratives' | 'job:extractEvidence'
  | 'job:draftAssessmentsIfFinal'
  | 'job:selectDebriefPicks'               // OD5-5: up to 3 picks for the released round (scoring selectDebriefPicks) → SSE debrief.picks_ready
  | 'sse:roundStateChanged' | 'sse:countdownChanged' | 'sse:roundReleased' | 'sse:roundRolledBack'
  | 'sse:resolutionFailed' | 'sse:boardRefresh' | 'sse:surpriseDelivered';

/** Target: a concrete state, or resolved from the event/row at runtime. */
type Target = RoundState | 'self' | 'pausedFrom' | 'rollbackTarget';

export interface TransitionSpec {
  readonly from: readonly RoundState[];
  readonly to: Target;
  readonly actors: readonly Actor[];
  readonly guards: readonly Guard[];
  readonly effects: readonly RoundEffect[];
}

/** Always appended: tx:persistTransition (round_transition + audit_log), bump state_version. */
export const ROUND_MACHINE: { readonly [E in RoundEventType]: TransitionSpec } = {
  OPEN: {
    from: ['draft'], to: 'open', actors: ['facilitator'],
    guards: ['cohortActive', 'previousRoundReleased', 'noOtherRoundInFlight', 'humanTeamsHaveCeoAndCro', 'humanTeamsHaveStrategy'],
    effects: ['tx:setCountdown', 'tx:deliverRoundStartEvents', 'sse:roundStateChanged', 'sse:countdownChanged', 'sse:boardRefresh'],
  },
  EXTEND: {
    from: ['open', 'awaitingSurprise'], to: 'self', actors: ['facilitator'], guards: ['addMinutesPositive', 'surpriseDeliveredIfAwaiting'],
    effects: ['tx:setCountdown', 'sse:countdownChanged'],
  },
  LOCK: { // the facilitator closes the round; force = close with unsigned teams (OD4-7: rescued drafts, else last attested / last year's plan)
    from: ['open'], to: 'locked', actors: ['facilitator'], guards: ['allHumanTeamsSubmittedOrForced', 'rescueTeamsRescuable'],
    effects: ['tx:clearCountdown', 'tx:applyDefaultEventChoices', 'tx:submitRescuedDraftsUnattested', 'tx:submitCloseDefaults', 'tx:generatePersonaSubmissions', 'sse:roundStateChanged', 'sse:countdownChanged', 'sse:boardRefresh'],
  },
  REOPEN: { // close-default, rescue and persona submissions made at lock are superseded by a later seq on re-lock
    from: ['locked'], to: 'open', actors: ['facilitator'], guards: ['noResultRunning'],
    effects: ['tx:setCountdown', 'sse:roundStateChanged', 'sse:countdownChanged', 'sse:boardRefresh'],
  },
  PAUSE: {
    from: ['open', 'locked', 'awaitingSurprise'], to: 'paused', actors: ['facilitator'], guards: [],
    effects: ['tx:freezeCountdowns', 'sse:roundStateChanged', 'sse:countdownChanged'],
  },
  RESUME: {
    from: ['paused'], to: 'pausedFrom', actors: ['facilitator'], guards: ['hasPausedFrom'],
    effects: ['tx:restoreCountdowns', 'sse:roundStateChanged', 'sse:countdownChanged'],
  },
  RESOLVE: {
    from: ['locked'], to: 'resolving', actors: ['facilitator'], guards: ['noResultRunning'],
    effects: ['tx:freezeInputsAndHash', 'job:resolveRound', 'sse:roundStateChanged', 'sse:boardRefresh'],
  },
  RESOLVE_SUCCEEDED: { // worker persisted bank_round_kpi/outcomes in its own tx before firing this
    from: ['resolving', 'resolvingPhase2'], to: 'resolved', actors: ['worker'], guards: ['resultIsCurrentGeneration', 'invariantsPassed'],
    effects: ['tx:markResultSucceeded', 'job:generateNarratives', 'job:extractEvidence', 'sse:roundStateChanged', 'sse:boardRefresh'],
  },
  RESOLVE_FAILED: { // re-RESOLVE: if the surprise was already delivered, the worker runs ONE pass with the stored responses (no surpriseAt)
    from: ['resolving', 'resolvingPhase2'], to: 'locked', actors: ['worker'], guards: ['resultIsCurrentGeneration'],
    effects: ['tx:markResultFailed', 'sse:resolutionFailed', 'sse:roundStateChanged'],
  },
  ABORT_RESOLVE: {
    from: ['resolving', 'resolvingPhase2'], to: 'locked', actors: ['facilitator'], guards: ['resultStale', 'reasonProvided'],
    effects: ['tx:markResultAborted', 'sse:resolutionFailed', 'sse:roundStateChanged', 'sse:boardRefresh'],
  },
  HOLD_SURPRISE: { // previewId = eventInjectionPreview with timing midRound
    from: ['draft', 'open', 'locked'], to: 'self', actors: ['facilitator'], guards: ['noSurpriseHeld'],
    effects: ['tx:holdSurprise', 'sse:boardRefresh'],
  },
  CANCEL_SURPRISE: {
    from: ['draft', 'open', 'locked'], to: 'self', actors: ['facilitator'], guards: ['surpriseNotDelivered'],
    effects: ['tx:cancelSurprise', 'sse:boardRefresh'],
  },
  SURPRISE_HALTED: { // worker: resolveRound(…, surpriseAt) returned awaitingEventResponse
    from: ['resolving'], to: 'awaitingSurprise', actors: ['worker'], guards: ['resultIsCurrentGeneration', 'surpriseNotDelivered'],
    effects: ['tx:markResultHalted', 'sse:roundStateChanged', 'sse:boardRefresh'],
  },
  RELEASE_SURPRISE: {
    from: ['awaitingSurprise'], to: 'self', actors: ['facilitator'], guards: ['surpriseNotDelivered'],
    effects: ['tx:deliverSurpriseEvent', 'sse:surpriseDelivered', 'sse:countdownChanged', 'sse:boardRefresh'],
  },
  CONTINUE_SURPRISE: {
    from: ['awaitingSurprise'], to: 'resolvingPhase2', actors: ['facilitator'],
    guards: ['surpriseDelivered', 'surpriseAnsweredOrForced', 'noResultRunning'],
    effects: ['tx:clearCountdown', 'tx:applyDefaultEventChoices', 'tx:supersedeHaltedResult', 'tx:freezeInputsAndHash', 'job:resolveRound', 'sse:roundStateChanged', 'sse:boardRefresh'],
  },
  RELEASE: { // narratives do not block: auto-publishable ones go out now, held ones on facilitator approval
    from: ['resolved'], to: 'released', actors: ['facilitator'], guards: ['narrativesGeneratedOrWaived'],
    effects: ['tx:publishMarket', 'tx:completeCohortIfFinal', 'job:selectDebriefPicks', 'job:draftAssessmentsIfFinal', 'sse:roundReleased', 'sse:roundStateChanged'],
  },
  ROLLBACK: {
    from: ['resolved', 'released'], to: 'rollbackTarget', actors: ['facilitator'], guards: ['laterRoundsDraft', 'reasonProvided', 'cohortNotCompleted'],
    effects: ['tx:supersedeResult', 'tx:setCountdown', 'sse:roundRolledBack', 'sse:roundStateChanged', 'sse:countdownChanged', 'sse:boardRefresh'],
  },
};

export interface RoundSnapshot { readonly state: RoundState; readonly pausedFrom: PausableState | null }
export type TransitionResult =
  | { ok: true; to: RoundState; pausedFrom: PausableState | null; effects: readonly RoundEffect[] }
  | { ok: false; code: 'INVALID_TRANSITION'; from: RoundState; event: RoundEventType }
  | { ok: false; code: 'GUARD_FAILED'; failed: readonly Guard[] }
  | { ok: false; code: 'FORBIDDEN_ACTOR'; actor: Actor };

/** Effects that only apply when the target is 'open' (rollback to locked needs no countdown). */
const OPEN_ONLY: readonly RoundEffect[] = ['tx:setCountdown', 'sse:countdownChanged'];

export function transition(s: RoundSnapshot, e: RoundEvent, actor: Actor, ctx: GuardContext): TransitionResult {
  const spec = ROUND_MACHINE[e.type];
  if (!spec.from.includes(s.state)) return { ok: false, code: 'INVALID_TRANSITION', from: s.state, event: e.type };
  if (!spec.actors.includes(actor)) return { ok: false, code: 'FORBIDDEN_ACTOR', actor };
  const failed = spec.guards.filter((g) => !GUARDS[g](ctx, e, s.state));
  if (failed.length) return { ok: false, code: 'GUARD_FAILED', failed };

  let to: RoundState;
  switch (spec.to) {
    case 'self': to = s.state; break;
    case 'pausedFrom': to = s.pausedFrom!; break; // guarded by hasPausedFrom
    case 'rollbackTarget': to = (e as Extract<RoundEvent, { type: 'ROLLBACK' }>).target; break;
    default: to = spec.to;
  }
  const pausedFrom: PausableState | null = e.type === 'PAUSE' ? (s.state as PausableState) : null;
  const effects = spec.to === 'rollbackTarget' && to !== 'open' ? spec.effects.filter((x) => !OPEN_ONLY.includes(x)) : spec.effects;
  return { ok: true, to, pausedFrom, effects };
}

type Writes = { readonly draft: boolean; readonly forecast: boolean; readonly submit: boolean; readonly eventResponse: boolean };
/** Participant write-permissions by state, FACILITATED cohorts (enforced in services → 423 ROUND_NOT_OPEN / ROUND_PAUSED). */
export const PARTICIPANT_WRITES: { readonly [S in RoundState]: Writes } = {
  draft: { draft: false, forecast: false, submit: false, eventResponse: false },
  open: { draft: true, forecast: true, submit: true, eventResponse: true },
  locked: { draft: false, forecast: false, submit: false, eventResponse: false }, // planned events are due at close
  resolving: { draft: false, forecast: false, submit: false, eventResponse: false },
  awaitingSurprise: { draft: false, forecast: false, submit: false, eventResponse: true }, // the delivered surprise only
  resolvingPhase2: { draft: false, forecast: false, submit: false, eventResponse: false },
  resolved: { draft: false, forecast: false, submit: false, eventResponse: false },
  released: { draft: false, forecast: false, submit: false, eventResponse: false },
  paused: { draft: false, forecast: false, submit: false, eventResponse: false },
};

// ───────────────────────────── staged finale (OD5-5) ─────────────────────────────
/**
 * Facilitated finale after the final release: the facilitator advances ONE stage at a time (finaleAdvance with the expected
 * current stage), in this fixed order — year-by-year replay vs the status-quo twin → AI banks revealed with their playbooks →
 * deal call → three unranked recognitions → rank LAST. No stage is skipped or revisited. Solo S4 shows the same sequence,
 * player-paced (no server stage).
 */
export const FINALE_STAGES = ['notStarted', 'replay', 'aiReveal', 'dealCall', 'recognitions', 'rank'] as const;
export type FinaleStage = (typeof FINALE_STAGES)[number];
export interface FinaleContext { readonly cohortStatus: 'setup' | 'active' | 'completed' | 'archived'; readonly current: FinaleStage }
export type FinaleAdvanceResult =
  | { ok: true; to: FinaleStage; effects: readonly ('tx:setFinaleStage' | 'tx:revealAiSeats' | 'tx:revealFinalRank' | 'sse:finaleStageChanged')[] }
  | { ok: false; code: 'FINALE_STAGE_MISMATCH'; current: FinaleStage };
/** Pure evaluator: advance from `expected` (must equal the stored stage) to the next stage, if the cohort is completed. */
export function advanceFinale(ctx: FinaleContext, expected: FinaleStage): FinaleAdvanceResult {
  const i = FINALE_STAGES.indexOf(ctx.current);
  const next = FINALE_STAGES[i + 1];
  if (ctx.cohortStatus !== 'completed' || expected !== ctx.current || next === undefined) return { ok: false, code: 'FINALE_STAGE_MISMATCH', current: ctx.current };
  const reveal = next === 'aiReveal' ? (['tx:revealAiSeats'] as const) : next === 'rank' ? (['tx:revealFinalRank'] as const) : ([] as const);
  return { ok: true, to: next, effects: ['tx:setFinaleStage', ...reveal, 'sse:finaleStageChanged'] };
}

// ───────────────────────────── solo variant ─────────────────────────────
/**
 * Solo: one human bank (the player holds every decision and is the CEO) vs 5 AI banks. No facilitator, no countdowns,
 * no close-by-deadline (so the OD4-7 close rule, rescue and 5-minute banner do not apply),
 * no pause (leaving = implicit pause; GET /solo/runs/:id resumes), no rollback in the pilot.
 * Submit auto-locks and auto-releases; the PLAYER starts the next round (NEXT_ROUND). Midround events HALT the
 * engine at month m until the player responds; CONTINUE re-runs resolveRound from the round-start state with all
 * responses (pure replay; no mid-round state is persisted). Narratives follow narrativeDisposition (CP-1).
 * Live surprises do not exist in solo. A stuck resolve is failed by the worker watchdog (pg-boss onFail).
 * Participant writes in solo key on the SOLO PHASE (SOLO_PARTICIPANT_WRITES), never on round.state.
 */
export const SOLO_PHASES = ['deciding', 'resolving', 'awaiting_event', 'reviewing', 'completed'] as const;
export type SoloPhase = (typeof SOLO_PHASES)[number];

/** Solo phase → stored round.state (same table, same queries as facilitated). */
export const SOLO_PHASE_TO_ROUND_STATE: { readonly [P in SoloPhase]: RoundState } = {
  deciding: 'open', resolving: 'resolving', awaiting_event: 'resolving', reviewing: 'released', completed: 'released',
};

/** Solo write-permissions by phase. awaiting_event: only the halted event may be answered. */
export const SOLO_PARTICIPANT_WRITES: { readonly [P in SoloPhase]: Writes } = {
  deciding: { draft: true, forecast: true, submit: true, eventResponse: true },
  resolving: { draft: false, forecast: false, submit: false, eventResponse: false },
  awaiting_event: { draft: false, forecast: false, submit: false, eventResponse: true },
  reviewing: { draft: false, forecast: false, submit: false, eventResponse: false },
  completed: { draft: false, forecast: false, submit: false, eventResponse: false },
};

export type SoloEvent =
  | { type: 'SUBMIT' }
  | { type: 'EVENT_HALT'; resultId: string; month: number }
  | { type: 'CONTINUE' }
  | { type: 'RESOLVE_SUCCEEDED'; resultId: string }
  | { type: 'RESOLVE_FAILED'; resultId: string; message: string }
  | { type: 'NEXT_ROUND' };
export type SoloEventType = SoloEvent['type'];

export interface SoloGuardContext {
  readonly roundNo: number;
  readonly roundCount: number;
  readonly forecastCommitted: boolean;             // the round's twin forecast is committed (OD5-1; no re-lock)
  readonly rationalesMissing: number;              // changed families without a rationale (OD4-4)
  readonly thesisMissing: boolean;                 // no family changed and no thesis line
  readonly croMemoAcknowledgedForDraftVersion: boolean;
  readonly roundStartEventsPending: number;
  readonly haltedEventResponded: boolean;
  readonly currentResultId: string | null;
  readonly resultInvariantsPassed: boolean | null;
}
export type SoloGuard =
  | 'forecastCommitted' | 'rationalesComplete' | 'thesisProvidedIfNoChange' | 'croMemoAcknowledgedForDraftVersion'
  | 'roundStartEventsResponded' | 'haltedEventResponded' | 'resultIsCurrentGeneration' | 'invariantsPassed'
  | 'moreRounds' | 'isFinalRound';
export const SOLO_GUARDS: { readonly [G in SoloGuard]: (c: SoloGuardContext, e: SoloEvent) => boolean } = {
  forecastCommitted: (c) => c.forecastCommitted,
  rationalesComplete: (c) => c.rationalesMissing === 0,
  thesisProvidedIfNoChange: (c) => !c.thesisMissing,
  croMemoAcknowledgedForDraftVersion: (c) => c.croMemoAcknowledgedForDraftVersion,
  roundStartEventsResponded: (c) => c.roundStartEventsPending === 0,
  haltedEventResponded: (c) => c.haltedEventResponded,
  resultIsCurrentGeneration: (c, e) => 'resultId' in e && e.resultId === c.currentResultId,
  invariantsPassed: (c) => c.resultInvariantsPassed === true,
  moreRounds: (c) => c.roundNo < c.roundCount,
  isFinalRound: (c) => c.roundNo >= c.roundCount,
};
export type SoloEffect =
  | 'tx:recordSubmission(source=solo)' | 'tx:settleScoredRunOnR1' | 'tx:generatePersonaSubmissions' | 'tx:freezeInputsAndHash' | 'job:resolveRound'
  | 'tx:recordHalt' | 'tx:deliverMidRoundEvent' | 'job:replayResolveWithResponses'
  | 'tx:markResultSucceeded' | 'tx:autoReleaseWithVerifiedNarrative' | 'job:extractEvidence' | 'tx:markResultFailed'
  | 'tx:openNextRound' | 'tx:deliverRoundStartEvents' | 'tx:completeCohort' | 'job:draftAssessment' | 'job:openPostAssessment'
  | 'sse:soloPhaseChanged';

export interface SoloTransitionSpec {
  readonly from: readonly SoloPhase[]; readonly to: SoloPhase; readonly actor: Extract<Actor, 'player' | 'worker'>;
  readonly guards: readonly SoloGuard[]; readonly effects: readonly SoloEffect[];
}
/**
 * Keys are unique transition names; NEXT_ROUND maps to nextRound | finish (exactly one passes its guards).
 * tx:settleScoredRunOnR1: at the R1 submit, the run becomes the assignment's scored run unless another run
 * of the same assignment is already scored; otherwise it is practice (excluded from evidence, pre/post, buyer views).
 */
export const SOLO_MACHINE = {
  submit: { from: ['deciding'], to: 'resolving', actor: 'player',
    guards: ['forecastCommitted', 'rationalesComplete', 'thesisProvidedIfNoChange', 'croMemoAcknowledgedForDraftVersion', 'roundStartEventsResponded'],
    effects: ['tx:recordSubmission(source=solo)', 'tx:settleScoredRunOnR1', 'tx:generatePersonaSubmissions', 'tx:freezeInputsAndHash', 'job:resolveRound', 'sse:soloPhaseChanged'] },
  eventHalt: { from: ['resolving'], to: 'awaiting_event', actor: 'worker', guards: ['resultIsCurrentGeneration'],
    effects: ['tx:recordHalt', 'tx:deliverMidRoundEvent', 'sse:soloPhaseChanged'] },
  continue: { from: ['awaiting_event'], to: 'resolving', actor: 'player', guards: ['haltedEventResponded'],
    effects: ['job:replayResolveWithResponses', 'sse:soloPhaseChanged'] },
  resolveSucceeded: { from: ['resolving'], to: 'reviewing', actor: 'worker', guards: ['resultIsCurrentGeneration', 'invariantsPassed'],
    effects: ['tx:markResultSucceeded', 'tx:autoReleaseWithVerifiedNarrative', 'job:extractEvidence', 'sse:soloPhaseChanged'] },
  resolveFailed: { from: ['resolving', 'awaiting_event'], to: 'deciding', actor: 'worker', guards: ['resultIsCurrentGeneration'],
    effects: ['tx:markResultFailed', 'sse:soloPhaseChanged'] },
  nextRound: { from: ['reviewing'], to: 'deciding', actor: 'player', guards: ['moreRounds'],
    effects: ['tx:openNextRound', 'tx:deliverRoundStartEvents', 'sse:soloPhaseChanged'] },
  finish: { from: ['reviewing'], to: 'completed', actor: 'player', guards: ['isFinalRound'],
    effects: ['tx:completeCohort', 'job:draftAssessment', 'job:openPostAssessment', 'sse:soloPhaseChanged'] },
} as const satisfies Record<string, SoloTransitionSpec>;
export type SoloTransitionName = keyof typeof SOLO_MACHINE;

/** Which solo transitions each API action / worker signal may take (in order). */
export const SOLO_EVENT_TRANSITIONS: { readonly [E in SoloEventType]: readonly SoloTransitionName[] } = {
  SUBMIT: ['submit'], EVENT_HALT: ['eventHalt'], CONTINUE: ['continue'],
  RESOLVE_SUCCEEDED: ['resolveSucceeded'], RESOLVE_FAILED: ['resolveFailed'], NEXT_ROUND: ['nextRound', 'finish'],
};

export type SoloTransitionResult =
  | { ok: true; name: SoloTransitionName; to: SoloPhase; effects: readonly SoloEffect[] }
  | { ok: false; code: 'INVALID_TRANSITION'; from: SoloPhase; event: SoloEventType }
  | { ok: false; code: 'GUARD_FAILED'; failed: readonly SoloGuard[] }
  | { ok: false; code: 'FORBIDDEN_ACTOR'; actor: Actor };

/** Solo evaluator (mirrors transition()): first candidate whose from/actor/guards all pass wins. */
export function soloTransition(phase: SoloPhase, e: SoloEvent, actor: Actor, ctx: SoloGuardContext): SoloTransitionResult {
  const candidates = SOLO_EVENT_TRANSITIONS[e.type].filter((n) => (SOLO_MACHINE[n].from as readonly SoloPhase[]).includes(phase));
  if (candidates.length === 0) return { ok: false, code: 'INVALID_TRANSITION', from: phase, event: e.type };
  let firstFailure: SoloTransitionResult | null = null;
  for (const name of candidates) {
    const spec: SoloTransitionSpec = SOLO_MACHINE[name];
    if (spec.actor !== actor) { firstFailure ??= { ok: false, code: 'FORBIDDEN_ACTOR', actor }; continue; }
    const failed = spec.guards.filter((g) => !SOLO_GUARDS[g](ctx, e));
    if (failed.length) { firstFailure ??= { ok: false, code: 'GUARD_FAILED', failed }; continue; }
    return { ok: true, name, to: spec.to, effects: spec.effects };
  }
  return firstFailure!;
}
